import React from "react"
import {
  createStyles,
  forwardRef,
  HTMLStyledProps,
  StyleProps,
  Styles,
} from "@design-system/styled-system"
import { Box } from "./Box"
import { BoxProps } from "./types"
import { Flex, FlexOptions } from "./Flex"

export type StackDirection = "row" | "column" | "row-reverse" | "column-reverse"

interface StackOptions extends FlexOptions {
  /**
   * The space between each stack item
   * @type StyleProps["margin"]
   */
  spacing?: StyleProps["margin"]
  /**
   * The direction to stack the items.
   */
  direction?: StackDirection
  /**
   * If `true`, each stack item will show a divider
   * @type React.ReactElement
   */
  divider?: React.ReactElement
  /**
   * If `true` the items will be stacked horizontally.
   */
  isInline?: boolean
}

export interface StackProps
  extends Omit<HTMLStyledProps<"div">, "direction">,
    StackOptions {}

export interface StackDividerProps extends HTMLStyledProps<"div"> {}

export const StackDivider = (props: StackDividerProps) => (
  <Box {...props} style={[styles.divider, props.style]} />
)
StackDivider.displayName = "StackDivider"

export const StackItem = (props: BoxProps) => (
  <Box {...props} style={[styles.item, props.style]} />
)
StackItem.displayName = "StackItem"

export const Stack = forwardRef<StackProps, "div">((props, ref) => {
  const {
    isInline,
    direction: directionProp,
    spacing: spacingProp,
    style,
    children,
    divider,
    ...rest
  } = props

  const direction = isInline ? "row" : directionProp ?? "column"
  // try to get the spacing value from design tokens first
  const spacing =
    typeof spacingProp === "string"
      ? Styles.theme.spaces[spacingProp]
      : spacingProp

  const directionSpacingStyles = {
    column: { marginTop: spacing },
    row: { marginLeft: spacing },
    "column-reverse": { marginBottom: spacing },
    "row-reverse": { marginRight: spacing },
  }

  const dividerSpacingStyles = {
    column: { marginVertical: spacing },
    row: { marginHorizontal: spacing },
    "column-reverse": { marginVertical: spacing },
    "row-reverse": { marginHorizontal: spacing },
  }

  const clones = React.Children.toArray(children).map((child, index) => {
    const isLast = index + 1 === React.Children.count(children)
    const spacingStyle =
      index > 0
        ? [directionSpacingStyles[direction], directionStyles[direction]]
        : undefined

    const _child = (
      <StackItem key={index} style={spacingStyle}>
        {child}
      </StackItem>
    )

    if (!divider) return _child

    const clonedDivider = React.cloneElement(
      divider as React.ReactElement<any>,
      {
        style: [
          divider?.props?.style,
          styles.divider,
          dividerStyles[direction],
          dividerSpacingStyles[direction],
        ],
      },
    )

    const _divider = isLast ? null : clonedDivider

    return (
      <React.Fragment key={index}>
        {_child}
        {_divider}
      </React.Fragment>
    )
  })

  return (
    <Flex {...rest} direction={direction} ref={ref}>
      {clones}
    </Flex>
  )
})

Stack.displayName = "Stack"

const styles = createStyles((theme) => ({
  divider: {
    borderWidth: theme.borderWidths.none,
    alignSelf: "stretch",
    borderStyle: "solid",
    borderColor: theme.borderColors.divider,
    width: "auto",
    height: "auto",
  },
  item: {
    display: "inline-block",
    flex: "0 0 auto",
    minWidth: 0,
  },
}))

const directionStyles = createStyles(() => ({
  column: {
    marginLeft: 0,
  },
  row: {
    marginTop: 0,
  },
  "column-reverse": {
    marginRight: 0,
  },
  "row-reverse": {
    marginBottom: 0,
  },
}))

const dividerStyles = createStyles((theme) => ({
  column: {
    marginHorizontal: 0,
    borderLeftWidth: theme.borderWidths.none,
    borderBottomWidth: theme.borderWidths.small,
  },
  "column-reverse": {
    marginHorizontal: 0,
    borderLeftWidth: theme.borderWidths.none,
    borderBottomWidth: theme.borderWidths.small,
  },
  row: {
    marginVertical: 0,
    borderLeftWidth: theme.borderWidths.small,
    borderBottomWidth: theme.borderWidths.none,
  },
  "row-reverse": {
    marginVertical: 0,
    borderLeftWidth: theme.borderWidths.small,
    borderBottomWidth: theme.borderWidths.none,
  },
}))

/**
 * A view that arranges its children in a horizontal line.
 */
export const HStack = forwardRef<StackProps, "div">((props, ref) => (
  <Stack align="center" {...props} direction="row" ref={ref} />
))
HStack.displayName = "HStack"

/**
 * A view that arranges its children in a vertical line.
 */
export const VStack = forwardRef<StackProps, "div">((props, ref) => (
  <Stack align="center" {...props} direction="column" ref={ref} />
))
VStack.displayName = "VStack"
